import gql from 'graphql-tag';
import { clients } from '../../util/clients';
import { fieldsToGqlString } from '../../util/shared/vue-global';

const { backend } = clients.direct;

const defaultFileDefinitionFields = {
  _id: '',
  Name: '',
  ImportExport: '',
  Program: '',
  FileType: '',
  Delimiter: '',
  TotalColumns: '',
  IncludeHeader: '',
  QuoteStrings: '',
  ColumnDefinition: {
    ColumnNumber: '',
    Name: '',
    StartPosition: '',
    EndPosition: '',
    Length: '',
    PaddingType: '',
    StripDecimal: '',
    FieldType: '',
    MapToTable: '',
    MapToColumn: '',
  },
};

export const FileDefinitionActions = {
  async loadFileDefinitions({ commit, dispatch }, {
    criteria, fields, baseCrudKey,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      const gqlfields = fields || defaultFileDefinitionFields;

      results = await backend.query({
        query: gql`query fileDefinitions($criteria: fileDefinitionCriteriaType) {
          fileDefinitions(criteria: $criteria) {
            ${fieldsToGqlString(gqlfields)}
          }
        }`,
        variables: { criteria },
        fetchPolicy: 'no-cache',
      });

      clients.handleGQLErrors(results, commit);
      if (results.data && results.data.fileDefinitions) {
        const fileDefinitions = results.data.fileDefinitions || [];
        commit('base/crud/setItems', [baseCrudKey, fileDefinitions], { root: true });
      }
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem loading the file definitions list.', { root: true });
    }
  },

  async upsertFileDefinition({ commit, dispatch, rootGetters }, {
    item, fields, baseCrudKey,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      const gqlfields = fields || defaultFileDefinitionFields;

      results = await backend.mutate({
        mutation: gql`mutation ($upsert: fileDefinitionInputType!) {
          upsertFileDefinition(upsert: $upsert) {
            ${fieldsToGqlString(gqlfields)}
          }
        }`,
        variables: { upsert: item },
        fetchPolicy: 'no-cache',
      });

      clients.handleGQLErrors(results, commit);
      if (results.data && results.data.upsertFileDefinition) {
        const updatedItem = results.data.upsertFileDefinition;
        const items = rootGetters['base/crud/items'](baseCrudKey);
        const updatedItems = items.map((i) => (i._id === updatedItem._id ? updatedItem : i));

        // If it's a new item (not in list), add it
        if (!items.find((i) => i._id === updatedItem._id)) {
          updatedItems.unshift(updatedItem);
        }

        commit('base/crud/setItems', [baseCrudKey, updatedItems], { root: true });
        dispatch('flashSuccess', 'File definition saved successfully.', { root: true });
      }
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem saving the file definition.', { root: true });
    }
  },

  async deleteFileDefinition({ commit, dispatch, rootGetters }, {
    _id, baseCrudKey,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.mutate({
        mutation: gql`mutation ($_id: ID!) {
          deleteFileDefinition(_id: $_id) {
            _id
          }
        }`,
        variables: { _id },
      });

      clients.handleGQLErrors(results, commit);
      if (results.data && results.data.deleteFileDefinition) {
        const items = rootGetters['base/crud/items'](baseCrudKey);
        const updatedItems = items.filter((i) => i._id !== _id);
        commit('base/crud/setItems', [baseCrudKey, updatedItems], { root: true });
        dispatch('flashSuccess', 'File definition deleted successfully.', { root: true });
      }
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem deleting the file definition.', { root: true });
    }
  },

  async loadAvailableTables({ commit, dispatch }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.query({
        query: gql`query availableTables {
          availableTables
        }`,
        fetchPolicy: 'no-cache',
      });

      clients.handleGQLErrors(results, commit);
      if (results.data && results.data.availableTables) {
        const tables = results.data.availableTables || [];
        commit('config/setAvailableTables', tables, { root: true });
        return tables;
      }
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem loading available tables.', { root: true });
    }
    return [];
  },

  async loadTableColumns({ commit, dispatch }, {
    tableName,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.query({
        query: gql`query tableColumns($tableName: String!) {
          tableColumns(tableName: $tableName)
        }`,
        variables: { tableName },
        fetchPolicy: 'no-cache',
      });

      clients.handleGQLErrors(results, commit);
      if (results.data && results.data.tableColumns) {
        const columns = results.data.tableColumns || [];
        commit('config/setTableColumns', { tableName, columns }, { root: true });
        return columns;
      }
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem loading table columns.', { root: true });
    }
    return [];
  },
};

export default FileDefinitionActions;
